'use client'

import { useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from 'next-auth/react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useAI } from '@/context/AIContext'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { CapabilityBubbles } from './CapabilityBubbles'
import { apiClient } from '@/lib/api-client'

// ============================================================================
// API response shape (matches backend ChatResponse)
// ============================================================================

interface ChatApiResponse {
  response: string
  tool_calls?: Array<{
    id: string
    name: string
    args: Record<string, unknown>
    result?: unknown
  }>
}

// ============================================================================

const PAGE_NAMES: Record<string, string> = {
  '/': 'Dashboard',
  '/workbench': 'Workbench',
  '/ai/policies': 'AI Policies',
  '/ai/insights': 'AI Insights',
  '/admin/users': 'Admin > Users',
  '/admin/roles': 'Admin > Roles',
  '/admin/groups': 'Admin > Groups',
  '/admin/sessions': 'Admin > Sessions',
  '/admin/events': 'Admin > Login Events',
  '/admin/audit': 'Admin > Audit Logs',
  '/admin/data-manager': 'Admin > Data Manager',
  '/admin/settings': 'Admin > Settings',
}

function getPageName(path: string): string {
  if (PAGE_NAMES[path]) return PAGE_NAMES[path]
  // Workbench ticket detail: /workbench/<id>
  if (path.startsWith('/workbench/') && path.length > '/workbench/'.length) {
    const ticketId = path.split('/').pop()
    return `Workbench > Ticket ${ticketId}`
  }
  // Admin sub-pages not in map
  if (path.startsWith('/admin/')) {
    const seg = path.replace('/admin/', '')
    return `Admin > ${seg.charAt(0).toUpperCase() + seg.slice(1)}`
  }
  // Fallback: title-case the last segment
  const parts = path.split('/').filter(Boolean)
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' > ') || 'Dashboard'
}

// ============================================================================
// Animation variants
// ============================================================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', damping: 25, stiffness: 300 },
  },
  exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.15 } },
}

// ============================================================================
// AIManager component
// ============================================================================

export function AIManager() {
  const { data: session } = useSession()
  const {
    isManagerOpen,
    closeManager,
    chatHistory,
    addMessage,
    updateLastAssistantMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    currentPageContext,
  } = useAI()

  // Derive ticket UUID when the user is on /workbench/<uuid>
  const currentTicketId = (() => {
    const match = currentPageContext.match(/^\/workbench\/([0-9a-f-]{36})$/i)
    return match ? match[1] : undefined
  })()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  // Gap 5: AbortController ref allows cancelling an in-flight stream on modal close
  const abortRef = useRef<AbortController | null>(null)

  // Auto-scroll to bottom when chat changes or AI is active
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory, isTyping])

  // Body scroll lock + Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isManagerOpen) {
        handleClose()
      }
    }

    if (isManagerOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  // handleClose is stable (useCallback below), so the effect doesn't loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagerOpen])

  // Focus the modal when it opens
  useEffect(() => {
    if (isManagerOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isManagerOpen])

  // Close handler — aborts any running stream before closing (Gap 5)
  const handleClose = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    setIsTyping(false)
    closeManager()
  }, [closeManager, setIsTyping])

  // ---- Message send logic (non-streaming) ---------------------------------

  const handleSendMessage = useCallback(async (content: string) => {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    const controller = new AbortController()
    abortRef.current = controller

    addMessage({ role: 'user', content })
    addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    try {
      const data = await apiClient.post<ChatApiResponse>(
        '/api/ai/chat',
        {
          message: content,
          history: chatHistory
            .filter(m => !m.isLoading)
            .map(m => ({ role: m.role, content: m.content })),
          context: {
            page: currentPageContext,
            ...(currentTicketId ? { ticket_id: currentTicketId } : {}),
          },
        },
        { signal: controller.signal },
      )

      const toolCalls = data.tool_calls?.map(tc => ({
        id: tc.id,
        name: tc.name,
        args: tc.args,
        result: tc.result,
      }))

      updateLastAssistantMessage({
        content: data.response,
        isLoading: false,
        toolCalls,
      })
      setIsTyping(false)
      abortRef.current = null
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      updateLastAssistantMessage({
        content:
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.',
        isLoading: false,
      })
      setIsTyping(false)
      abortRef.current = null
    }
  }, [
    addMessage,
    chatHistory,
    currentPageContext,
    currentTicketId,
    setIsTyping,
    updateLastAssistantMessage,
  ])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose()
  }

  const isDisabled = isTyping

  if (typeof window === 'undefined') return null

  return createPortal(
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="ai-manager-backdrop"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop flex items-center justify-center p-4 sm:p-8 md:p-12"
            style={{
              backgroundColor: 'rgba(26, 35, 64, 0.55)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          >
            {/* Modal */}
            <motion.div
              ref={modalRef}
              key="ai-manager-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-manager-title"
              aria-describedby="ai-manager-description"
              tabIndex={-1}
              onClick={e => e.stopPropagation()}
              className={cn(
                'z-modal relative w-full max-w-2xl',
                'h-[80vh] max-h-[700px] min-h-[450px]',
                'bg-white border border-gray-200 rounded-2xl shadow-2xl',
                'flex flex-col overflow-hidden',
              )}
            >
              {/* Decorative gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-cornflower/20 via-transparent to-brand-purple/20 pointer-events-none" />

              {/* ---- Header ---- */}
              <div className="relative border-b border-border/30 px-6 py-5">
                <div className="flex items-center justify-between">
                  {/* Identity */}
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-xl',
                        'bg-gradient-to-br from-brand-navy via-brand-purple to-brand-cornflower',
                        'shadow-lg shadow-brand-purple/25 ring-2 ring-white/50',
                      )}
                      aria-hidden="true"
                    >
                      <Icons.sparkles className="h-6 w-6 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h2
                        id="ai-manager-title"
                        className="text-xl font-semibold text-brand-navy"
                      >
                        AuthID AI Assistant
                      </h2>
                      <p
                        id="ai-manager-description"
                        className="text-sm text-muted-foreground"
                      >
                        Your document verification support co-pilot
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {chatHistory.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHistory}
                        disabled={isDisabled}
                        className="text-muted-foreground hover:text-foreground gap-1.5"
                        aria-label="Clear conversation"
                        title="Clear conversation"
                      >
                        <Icons.trash className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                        <span className="hidden sm:inline">Clear</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleClose}
                      className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
                      aria-label="Close AI assistant (Esc)"
                      title="Close (Esc)"
                    >
                      <Icons.close className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                    </Button>
                  </div>
                </div>

                {/* Context badge */}
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <div
                    className={cn(
                      'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
                      'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
                      'text-brand-navy border border-brand-cornflower/20 text-sm font-medium',
                    )}
                    role="status"
                    aria-label={`Currently viewing: ${getPageName(currentPageContext)}`}
                  >
                    <Icons.globe className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} aria-hidden="true" />
                    <span>Viewing: {getPageName(currentPageContext)}</span>
                  </div>

                  <kbd
                    className={cn(
                      'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
                      'text-xs font-medium',
                      'border border-border/50 bg-muted/50 text-muted-foreground',
                    )}
                    aria-label="Keyboard shortcut: Cmd J to toggle"
                  >
                    <Icons.command className="h-3 w-3 mr-1" aria-hidden="true" />
                    J to toggle
                  </kbd>
                </div>
              </div>

              {/* ---- Messages ---- */}
              <div
                className="relative flex-1 overflow-y-auto px-6 py-5"
                role="log"
                aria-live="polite"
                aria-label="Chat history"
              >
                {chatHistory.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center py-8">
                    <div
                      className={cn(
                        'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
                        'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
                        'ring-1 ring-brand-cornflower/30',
                      )}
                      aria-hidden="true"
                    >
                      <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
                    </div>

                    <h3 className="font-display text-xl font-semibold text-brand-navy">
                      How can I help you?
                    </h3>
                    <p className="mt-2 max-w-sm text-muted-foreground">
                      Ask anything about the AuthID platform, tickets, or search the web.
                    </p>

                    <div className="mt-8 w-full max-w-lg">
                      <CapabilityBubbles onSelect={handleSendMessage} />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatHistory.map(message => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        userName={session?.user?.name || undefined}
                        userImage={session?.user?.image}
                      />
                    ))}
                    <div ref={messagesEndRef} aria-hidden="true" />
                  </div>
                )}
              </div>

              {/* ---- Input ---- */}
              <div className="relative border-t border-border/30">
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isDisabled}
                  placeholder={
                    isTyping ? 'AI is thinking…' : 'Ask anything about AuthID…'
                  }
                />
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}
